import { intl } from '@intl';
import { isEuEnv, getUserLang } from '@mb/lib';
import { definedTranslation } from '@productSearch/i18n';
import { getProjects } from '@projectsApi';

const {
  addToCartLabel,
  seeSimilarLabel,
  availabilityRestrictedTooltip,
  geoRestrictedTooltip,
  projectRestrictedTooltip,
} = definedTranslation.card;

const userLang = getUserLang();
const langPrefix = isEuEnv ? `/${userLang}` : '';

function initAddToCart() {
  return {
    added: false,
    hover: false,
    customer: null,
    projectId: false,
    projectList: false,
    projectName: false,
    addToCartLabel: 'Add to Cart',
    seeSimilarLabel: 'See Similar',
    projectsSetListUrl: '/api/v1/projects',
    addToCartUrl: `${langPrefix}/sdg_cart/lineitem/addmultiple`,
    diverzifyUrl: '/mit_diverzify/diverzify',
    productName: productAttributeJson['name'].value
      ? productAttributeJson['name'].value
      : 0,
    productId: productAttributeJson['product_id'].value
      ? productAttributeJson['product_id'].value
      : 0,
    productSku: productAttributeJson['sku'].value
      ? productAttributeJson['sku'].value
      : 0,
    inventory: [],

    tier13GroupId: 13,
    init() {
      this.getCustomerData();
    },
    getCustomerData() {
      try {
        const rawStorage = localStorage.getItem('mage-cache-storage');

        if (!rawStorage) {
          this.customer = null;

          return;
        }

        const storage = JSON.parse(rawStorage);

        // Ensure storage is an object before accessing properties
        if (typeof storage === 'object' && storage !== null) {
          this.customer = storage?.customer || null;

          return;
        }

        this.customer = null;
      } catch (error) {
        console.warn('Failed to parse mage-cache-storage:', error);
        this.customer = null;
      }
    },
    shouldTriggerProjectSelectionDialog() {
      const selectProjectTimestamp = JSON.parse(
        localStorage.getItem('select-project-timestamp') ?? '0',
      );
      const userData =
        JSON.parse(localStorage.getItem('mage-cache-storage') || '{}')
          ?.customer || {};
      const currentTimestamp = Date.now();
      const isTier13 = userData?.groupId === this.tier13GroupId;

      if (isTier13) {
        return false;
      }

      return currentTimestamp - selectProjectTimestamp > 86400000; // 24 hours
    },

    addToCart() {
      const self = this;

      if (!self.customer) {
        window.dispatchEvent(new CustomEvent('open-visitor-modal'));

        return;
      }

      if (this.projectList && this.projectList.length <= 0) {
        this.dispatchCreateProject();

        return;
      }

      if (this.shouldTriggerProjectSelectionDialog()) {
        window.dispatchEvent(
          new CustomEvent('open-project-selection-dialog', {
            detail: {
              currentProjectId: self?.customer?.current_project,
              analyticsEventLocation: 'Product detail page',
              onCurrentProjectSave: (project) => {
                window.dispatchEvent(
                  new CustomEvent('project-selected', {
                    detail: { project },
                  }),
                );

                this.prepareAddToCart(
                  project?.externalId || project?.project_id,
                  project?.title || project?.project_title,
                  project?.type?.externalId?.toString() ||
                    project?.project_type,
                );
              },
            },
          }),
        );

        return;
      }

      const isRepUser = this.isRepUser();
      const userData = self?.customer;
      const projectData = {
        projectId: userData?.current_project,
        projectName: userData?.current_project_name,
        projectType: userData?.current_project_type,
        phaseId: userData?.current_project_phase_id,
        location: userData?.current_project_location,
        dueYear: userData?.current_project_due_year,
        dueMonth: userData?.current_project_due_month,
        city: userData?.current_project_city,
      };

      const requiredFields = [
        'projectType',
        'location',
        'phaseId',
        'dueMonth',
        'dueYear',
        'city',
      ];
      const isMissingRequired = requiredFields.some(
        (field) => !projectData[field] && !isRepUser,
      );

      if (isMissingRequired) {
        window.dispatchEvent(
          new CustomEvent('project-edit-dialog-open-event', {
            detail: {
              projectId: projectData.projectId,
              eventLocation: 'Product detail page',
              successCallback: (projectUpdated) => {
                const project = {
                  projectId:
                    projectUpdated?.externalId || projectUpdated?.project_id,
                  projectName:
                    projectUpdated?.title ||
                    projectUpdated?.project_name ||
                    projectUpdated?.project_title,
                  projectType:
                    projectUpdated?.type?.externalId ||
                    projectUpdated?.project_type,
                  phaseId:
                    projectUpdated?.phase?.code ||
                    projectUpdated?.project_phase_id ||
                    projectUpdated?.phase_id,
                  location:
                    projectUpdated?.project_location ||
                    projectUpdated?.country?.code ||
                    projectUpdated?.state?.code,
                  dueYear: projectUpdated?.dueYear || projectUpdated?.due_year,
                  dueMonth:
                    projectUpdated?.dueMonth || projectUpdated?.due_month,
                  city: projectUpdated?.city,
                };

                this.prepareAddToCart(
                  project?.projectId,
                  project?.projectName,
                  project?.projectType,
                );
              },
            },
          }),
        );

        return;
      }

      this.prepareAddToCart(
        self?.customer?.current_project,
        self?.customer?.current_project_name,
        self?.customer?.current_project_type,
      );
    },
    prepareAddToCart(projectId, projectName, projectType) {
      if (
        typeof window.productConfigurableJson !== 'undefined' &&
        window.productConfigurableJson['child_list'] &&
        window.productConfigurableJson['child_list'][this.productId] &&
        window.productConfigurableJson['child_list'][this.productId][
          'product_finish_type'
        ].value === 'finish_material'
      ) {
        this.dispatchAssociatedFinishesModal();
        return;
      }

      if (this.isRestricted()) {
        this.dispatchRestrictedItemModal(projectId);
        return;
      }

      this.dispatchAddToCart(projectId, projectName, projectType);
    },
    isRepUser() {
      return this.customer?.groupId === 13;
    },

    isGpsCompliant() {
      /**
       * `gps_eligible` model
       * productAttributeJson?.gps_eligible = {
       code: 'gps_eligible' | null,
       label: string,
       value: string | null - 'Yes'
       };
       */
      return productAttributeJson?.gps_eligible?.value?.toLowerCase() === 'yes';
    },

    getRestrictedType() {
      if (this.isUnavailable()) {
        return 'unavailable_restricted_type';
      }

      if (this.isGeoRestricted()) {
        return 'geo_restricted_type';
      }
      if (this.isProjectRestricted()) {
        return 'project_restricted_type';
      }
    },

    dispatchRestrictedItemModal(projectId) {
      window.dispatchEvent(
        new CustomEvent('open-restricted-item-modal', {
          detail: {
            restrictionType: this.getRestrictedType(),
            title: productAttributeJson.manufacturer?.value,
            subtitle1: productAttributeJson.taxonomy_us?.value
              ?.split('|')
              .slice(0, 1)
              ?.pop()
              ?.split('.')
              ?.pop(),
            subtitle2: this.productName,
            entityId: this.productId,
            material: {
              sku: productAttributeJson.sku?.value,
              manufacturer_sku: productAttributeJson.manufacturer_sku?.value,
              manufacturer: productAttributeJson.manufacturer?.value,
              isGpsCompliant: this.isGpsCompliant(),
            },

            imgUrl:
              window.productConfigurableJson['images'][this.productId][0]?.url,
            projectId: projectId,
          },
        }),
      );
    },
    dispatchAssociatedFinishesModal() {
      window.dispatchEvent(
        new CustomEvent('open-associated-finishes-products-modal', {
          detail: {
            associatedProducts:
              window.productConfigurableJson['child_list'][this.productId][
                'associated_finish_products'
              ]?.value,
            parentProduct: {
              id: this.productId,
              name: window.productConfigurableJson['child_list'][
                this.productId
              ]['name']?.value,
              manufacturerName:
                window.productConfigurableJson['child_list'][this.productId][
                  'manufacturer'
                ]?.value,
              productImageUrl:
                window.productConfigurableJson['images'][this.productId][0]
                  ?.url,
            },
            analyticsEventData: {
              item_list_id: pdpUrlParams.get('itemListId'),
              item_list_name: pdpUrlParams.get('itemListName')
                ? decodeURI(pdpUrlParams.get('itemListName'))
                : null,
              index: pdpUrlParams.get('index'),
            },
          },
        }),
      );
    },
    dispatchCreateProject() {
      const self = this;
      self.openCreateProject();
    },
    openCreateProject() {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            successCallback: (projectData) => {
              const project = {
                projectId: projectData?.externalId || projectData?.project_id,
                projectName:
                  projectData?.title ||
                  projectData?.project_name ||
                  projectData?.project_title,
                projectType:
                  projectData?.type?.externalId || projectData?.project_type,
              };

              this.prepareAddToCart(
                project.projectId,
                project.projectName,
                project.projectType,
              );
            },
          },
        }),
      );
    },
    isRecommendationsModalEnabled() {
      const browserStorage = JSON.parse(
        hyva.getBrowserStorage()?.getItem('recommendations-modal-settings'),
      );
      if (!browserStorage || this.isPaintAttributeSet()) {
        return false;
      }

      const allowedGroups = browserStorage.hasOwnProperty('allowed-groups')
        ? browserStorage['allowed-groups']
        : [];
      return allowedGroups.includes(this.customer.groupId.toString());
    },
    openRecommendationsModal(data, project, inProgress = false) {
      const inventoryProduct = this.inventory
        ? Object.values(this.inventory).find(
            (item) => item.product_id == this.productId,
          )
        : null;

      const addedMaterial =
        data && data.items
          ? Object.values(data?.items).find(
              (item) =>
                item.sku === this.productSku && item?.product?.thumbnail !== '',
            )
          : null;

      const catalogMediaPath = MEDIA_URL + 'catalog/product';
      let addedMaterialImage = addedMaterial
        ? catalogMediaPath + addedMaterial.product.thumbnail
        : null;
      let controlRuleId = null;
      let guardrailsDailyLimitReached = false;

      if (data && data.hasOwnProperty('message') && data.message) {
        if (data?.modal_data != null) {
          addedMaterialImage = this.formatControlRulesImageUrl(
            data?.modal_data?.[0]?.image_url ?? '',
          );
          controlRuleId = data.modal_data?.[0]?.control_rule_id || null;
          guardrailsDailyLimitReached =
            data.modal_data?.[0]?.guardrails_daily_limit_reached || false;
        } else if (data?.productThumbnail != null) {
          addedMaterialImage = data?.productThumbnail
            ? catalogMediaPath + data?.productThumbnail
            : '';
        }
      }

      const detail = {
        inProgress: inProgress,
        serverMessage: data?.message ?? null,
        isInRealTimeStock: inventoryProduct?.qty > 0,
        skus: [this.productId],
        bagProject: project,
        addedMaterialImage: addedMaterialImage ?? null,
        controlRuleId: controlRuleId,
        forceAllow: data?.forceAllow ?? false,
        guardrailsDailyLimitReached: guardrailsDailyLimitReached,
      };

      if (data && data.hasOwnProperty('service')) {
        detail.service = data.service;

        window.dispatchEvent(
          new CustomEvent('open-services-modal', {
            detail: detail,
          }),
        );
      } else {
        // Apply GPS compliance condition for SIM modal
        // For Gensler users: only show if product is NOT GPS compliant
        // For all other users: always show (existing logic)

        const shouldAllowRecommendationsModal =
          this.shouldRenderSimilarItemsForGenslerProduct(this.isGpsCompliant());

        window.dispatchEvent(
          new CustomEvent('open-recommendations-modal', {
            detail: {
              ...detail,
              shouldHideRecommendations: !shouldAllowRecommendationsModal,
            },
          }),
        );
      }
    },
    shouldRenderSimilarItemsForGenslerProduct(isGpsCompliant) {
      const isGenslerCustomer = this.isGenslerCustomer();
      // Show similar items for non-Gensler customers
      if (!isGenslerCustomer) {
        return true;
      }

      // For Gensler customers, only show if product is Non-GPS compliant
      return !isGpsCompliant;
    },
    isGenslerCustomer() {
      const genslerEmailDomain = '@gensler.com';

      if (!this.customer?.email) {
        return false;
      }

      const emailLowerCase = this.customer.email.toLowerCase();

      // Ensure the domain is at the end of the email
      return emailLowerCase.endsWith(genslerEmailDomain);
    },
    dispatchAddToCart(projectId, projectName, projectType) {
      const self = this;
      const project = {
        project_title: projectName,
        project_type: projectType,
        project_id: projectId,
      };
      self.startLoader(true);

      if (this.isRecommendationsModalEnabled()) {
        this.openRecommendationsModal(null, project, true);
      }

      const itemListName = pdpUrlParams.get('itemListName')
        ? decodeURI(pdpUrlParams.get('itemListName'))
        : null;
      fetch(self.addToCartUrl, {
        method: 'POST',
        body:
          'product_id[0]=' +
          self.productId +
          '&' +
          'project_id[0]=' +
          projectId +
          '&' +
          'project_name[0]=' +
          projectName +
          '&' +
          'project_type[0]=' +
          projectType +
          '&' +
          'item_list_id[0]=' +
          pdpUrlParams.get('itemListId') +
          '&' +
          'item_list_name[0]=' +
          itemListName +
          '&' +
          'index[0]=' +
          pdpUrlParams.get('index') +
          '&' +
          'qty[0]=1',
        headers: {
          'Content-type': 'application/x-www-form-urlencoded',
        },
      })
        .then((response) => {
          return response.json();
        })
        .then((res) => {
          if (res.status === 'success') {
            const inventoryProduct = this.inventory
              ? Object.values(this.inventory).find(
                  (item) => item.product_id === this.productId,
                )
              : null;

            if (
              (Number(inventoryProduct?.qty) || 0) <= 0 &&
              this.isRecommendationsModalEnabled()
            ) {
              this.openRecommendationsModal(res, project, false);

              if (!res?.modal_data && !res?.message) {
                this.gtmTrackAddToCart();
              }
            } else {
              if (res?.message) {
                const project = {
                  project_title: projectName,
                  project_type: projectType,
                  project_id: projectId,
                };
                res.forceAllow = true;
                this.openRecommendationsModal(res, project, false);
              } else {
                if (inventoryProduct?.qty > 0) {
                  this.checkDiverzify(res, project);

                  // Only show success message if the product is in stock ?>
                  // Otherwise the message will be shown in the recommendations modal ?>
                  dispatchMessages(
                    [
                      {
                        type: 'success',
                        text: 'Added to cart',
                      },
                    ],
                    5000,
                  );
                }
                self.added = true;
                this.gtmTrackAddToCart();
              }
            }

            setTimeout(() => {
              hyva.setCookie('mage-cache-sessid', '', -1, true); // remove the cookie to force customer section
              window.dispatchEvent(
                new CustomEvent('reload-customer-section-data'),
              );
            }, 800);
          } else {
            dispatchMessages(
              [
                {
                  type: 'error',
                  text: res.message || 'Some error occurred',
                },
              ],
              5000,
            );
          }
        })
        .finally(() => {
          setTimeout(() => {
            self.added = false;
          }, 5000);
          self.startLoader(false);
        });
    },
    /* Reload Attribute Data from Event  */
    getReloadAttribute(productId) {
      if (productId && typeof window.productConfigurableJson !== 'undefined') {
        if (
          window.productConfigurableJson['child_list'][productId] !== undefined
        ) {
          this.productName =
            window.productConfigurableJson['child_list'][productId][
              'name'
            ]?.value;
          this.productSku =
            window.productConfigurableJson['child_list'][productId][
              'sku'
            ]?.value;
          this.productId = productId;
        }
      }
    },
    /* condition to show Add to cart */
    showAddToCart() {
      const isDigital = productAttributeJson.sample_type?.value === 'Digital';
      return (
        !isDigital &&
        productAttributeJson['product_finish_type'].value !==
          'finish_product' &&
        !productAttributeJson['is_paint_product']?.value
      );
    },
    isPaintAttributeSet() {
      return productAttributeJson['is_paint_attribute_set'].value;
    },
    /* Receive data from private content */
    receiveData(data) {
      if (data && data.customer?.firstname) {
        this.customer = data && data.customer ? data.customer : false;
      }
    },
    isGeoRestricted() {
      if ('US' === ENVIRONMENT_REGION) {
        let states =
          productAttributeJson['state_permission']?.value?.split(', ');
        if (states && states.length > 0) {
          return !states.includes(this.customer?.state);
        }
      }
      if ('EU' === ENVIRONMENT_REGION) {
        let countries =
          productAttributeJson['country_permissions']?.value?.split(', ');
        if (countries && countries.length > 0) {
          return !countries.includes(this.customer?.country);
        }
      }
      return false;
    },

    isProjectRestricted() {
      const restrictions = productAttributeJson['project_type_restrictions'];
      return (
        Array.isArray(restrictions) &&
        restrictions.includes(this?.customer?.current_project_type)
      );
    },

    isUnavailable() {
      return productAttributeJson['is_unavailable']?.value;
    },

    isRestricted() {
      return (
        this.isUnavailable() ||
        this.isGeoRestricted() ||
        this.isProjectRestricted()
      );
    },

    getAddToCartLabel() {
      if (this.isRestricted()) {
        return intl.formatMessage(seeSimilarLabel);
      }

      return intl.formatMessage(addToCartLabel);
    },
    getAddToCartLabelTooltip() {
      if (this.isUnavailable()) {
        return intl.formatMessage(availabilityRestrictedTooltip);
      }

      if (this.isGeoRestricted()) {
        return intl.formatMessage(geoRestrictedTooltip);
      }

      if (this.isProjectRestricted()) {
        return intl.formatMessage(projectRestrictedTooltip);
      }
      return '';
    },
    getAddToCartLabelOnOver() {
      if (this.isRestricted()) {
        return intl.formatMessage(seeSimilarLabel);
      }
      return intl.formatMessage(addToCartLabel);
    },

    getProjects(projectList) {
      this.projectList = projectList;
    },
    getInventory(data) {
      this.inventory = data;
    },
    startLoader(isLoading) {
      window.dispatchEvent(
        new CustomEvent('start-loader', { detail: isLoading }),
      );
    },
    gtmTrackAddToCart() {
      if (typeof mitGTM !== 'undefined') {
        let item = {},
          itemData = productAttributeJson,
          productId = this.productId;
        if (
          productAttributeJson['product_type'].value === 'configurable' &&
          typeof window.productConfigurableJson !== 'undefined' &&
          typeof window.productConfigurableJson['child_list'][productId] !==
            'undefined'
        ) {
          itemData = window.productConfigurableJson['child_list'][productId];
        }

        const listParams = {
          item_list_id: pdpUrlParams.get('itemListId'),
          item_list_name: pdpUrlParams.get('itemListName')
            ? decodeURI(pdpUrlParams.get('itemListName'))
            : null,
          index: pdpUrlParams.get('index')
            ? parseInt(pdpUrlParams.get('index'))
            : null,
        };

        item = mitGTM.prepareGTMItemData({ ...itemData, ...listParams });
        mitGTM.addToCart([item], 'Product detail page');
      }
    },
    formatControlRulesImageUrl(strUrl) {
      return strUrl.startsWith('//') ? `https:${strUrl}` : strUrl;
    },
    checkDiverzify(addToCartResponse, project) {
      const formData = new FormData();
      formData.append('product_id', this.productId);
      fetch(this.diverzifyUrl, {
        method: 'POST',
        body: formData,
      })
        .then((response) => {
          return response.json();
        })
        .then((res) => {
          if (res?.data?.hasOwnProperty('customer_3m_dealer')) {
            addToCartResponse.service = res;
            this.openRecommendationsModal(addToCartResponse, project, false);
          }
        });
    },
    async loadProjects() {
      const response = await getProjects();

      this.projectList = response.data.projects.items.map((item) => {
        return { ...item, project_id: String(item.externalId) };
      });
    },

    handleProjectChange(projectId) {
      const newProject = this.getProjectById(projectId);
      if (!newProject) {
        return;
      }
      this.setCurrentProject(newProject);
    },
    setCurrentProject(newProject) {
      this.projectId = newProject.project_id;
      this.projectName = newProject.title;
      this.projectType = newProject.type;
      this.customer.current_project = newProject.project_id;
      this.customer.current_project_name = newProject.title;
      this.customer.current_project_type = newProject.type;
    },
    handleProjectCreate(project) {
      const mappedProject = this.mapProject(project);
      this.projectList.push(mappedProject);
    },
    handleProjectUpdate(project) {
      const mappedProject = this.mapProject(project);
      const index = this.projectList.findIndex(
        (p) => p.project_id === mappedProject.project_id,
      );
      if (index !== -1) {
        this.projectList[index] = mappedProject;
      } else {
        this.projectList.push(mappedProject);
      }
      if (this.customer.current_project === mappedProject.project_id) {
        this.setCurrentProject(mappedProject);
      }
    },
    mapProject(project) {
      return {
        project_id: project.externalId.toString(),
        title: project.title,
        type: project.type.externalId,
      };
    },
    getProjectById(projectId) {
      return this.projectList.find(
        (project) => project.project_id === projectId,
      );
    },
  };
}

window.initAddToCart = initAddToCart;
